(function () {
  var initializedForms = new WeakSet();
  var addressErrorMessage = "Please select an address from the Google suggestions.";

  function findField(form, name) {
    return form.querySelector('[data-google-address="' + name + '"]');
  }

  function fieldValue(component, useShortName) {
    return useShortName ? component.shortText : component.longText;
  }

  function addressComponent(place, type, useShortName) {
    var components = place.addressComponents;

    if (!components) {
      return "";
    }

    for (var i = 0; i < components.length; i += 1) {
      var component = components[i];
      if (component.types.indexOf(type) !== -1) {
        return fieldValue(component, useShortName);
      }
    }

    return "";
  }

  function setFieldValue(field, value) {
    if (!field) {
      return;
    }

    field.value = value;
    field.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function lockAddressField(field) {
    if (!field) {
      return;
    }

    field.setAttribute("aria-readonly", "true");
    field.setAttribute("tabindex", "-1");
    field.classList.add("google-address-locked-field");

    if (field.tagName === "SELECT") {
      field.style.pointerEvents = "none";
    } else {
      field.readOnly = true;
    }

    field.addEventListener("keydown", function (event) {
      event.preventDefault();
    });
  }

  function showAddressError(form) {
    var error = findField(form, "error");
    var address1 = findField(form, "address_1");

    if (!error && address1) {
      error = document.createElement("small");
      error.className = "form-text text-danger google-address-error";
      error.setAttribute("data-google-address", "error");
      address1.parentNode.appendChild(error);
    }

    if (error) {
      error.textContent = addressErrorMessage;
      error.classList.remove("d-none");
    }

    if (address1) {
      address1.classList.add("is-invalid");
      address1.focus();
    }
  }

  function hideAddressError(form) {
    var error = findField(form, "error");
    var address1 = findField(form, "address_1");

    if (error) {
      error.classList.add("d-none");
    }

    if (address1) {
      address1.classList.remove("is-invalid");
    }
  }

  function submitButtons(form) {
    return form.querySelectorAll('button[type="submit"], input[type="submit"]');
  }

  function enableSubmitButtons(form) {
    var buttons = submitButtons(form);
    for (var i = 0; i < buttons.length; i += 1) {
      buttons[i].disabled = false;
      buttons[i].removeAttribute("disabled");
    }
  }

  function clearPlaceSelection(form) {
    var placeId = findField(form, "place_id");

    if (placeId) {
      placeId.value = "";
    }
  }

  function fillAddressFields(form, place) {
    var streetNumber = addressComponent(place, "street_number", false);
    var route = addressComponent(place, "route", false);
    var subpremise = addressComponent(place, "subpremise", false);
    // 5-digit ZIP only. The prefill used to append the +4 suffix, which Selection
    // rejects — and a programmatic .value set bypasses the field's own 5-digit mask.
    var postalCode = addressComponent(place, "postal_code", false).replace(/\D/g, "").slice(0, 5);
    var city = addressComponent(place, "locality", false) ||
      addressComponent(place, "postal_town", false) ||
      addressComponent(place, "sublocality_level_1", false) ||
      addressComponent(place, "administrative_area_level_3", false) ||
      // Unincorporated communities (e.g. Porter, TX) have no locality;
      // Google tags the postal city as "neighborhood". Must stay last so
      // NYC-style boroughs (sublocality_level_1) win over neighborhoods.
      addressComponent(place, "neighborhood", false);
    var state = addressComponent(place, "administrative_area_level_1", true);
    var address1 = [streetNumber, route].filter(Boolean).join(" ");

    setFieldValue(findField(form, "address_1"), address1);
    setFieldValue(findField(form, "address_2"), subpremise);
    setFieldValue(findField(form, "city"), city);
    setFieldValue(findField(form, "state"), state);
    setFieldValue(findField(form, "zipcode"), postalCode);
    setFieldValue(findField(form, "place_id"), place.id || (place.toJSON && place.toJSON().id) || "");
    hideAddressError(form);
  }

  // Mounts the new google.maps.places.PlaceAutocompleteElement. Unlike the legacy
  // Autocomplete widget, this element renders its own input and cannot be attached
  // to the existing address_1 field, so we inject it into a dedicated container
  // (data-google-address="autocomplete"), falling back to placing it before
  // the address_1 field if no container is present.
  function mountAutocomplete(form, address1) {
    var placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
      // US only — background checks can't be run on a non-US address.
      includedRegionCodes: ["us", "pr", "vi"],
      // The legacy widget used types: ["address"]; PlaceAutocompleteElement has no
      // direct equivalent for that collection, so region codes are the only filter.
    });

    placeAutocomplete.classList.add("google-address-autocomplete-input");
    // The element is a Google web component that otherwise follows the OS/browser
    // dark-mode setting (rendering with a dark background while the rest of the
    // Bootstrap form stays light). Force a light scheme and lay it out as a normal
    // full-width block so it doesn't overlap the Address 1 field below it.
    placeAutocomplete.style.colorScheme = "light";
    placeAutocomplete.style.display = "block";
    placeAutocomplete.style.width = "100%";
    placeAutocomplete.style.height = "calc(1.5em + 0.75rem + 2px)";
    placeAutocomplete.style.minHeight = "calc(1.5em + 0.75rem + 2px)";
    placeAutocomplete.style.maxHeight = "calc(1.5em + 0.75rem + 2px)";
    placeAutocomplete.placeholder = "Search for your address";

    var container = findField(form, "autocomplete");
    if (container) {
      container.appendChild(placeAutocomplete);
    } else {
      address1.parentNode.insertBefore(placeAutocomplete, address1);
    }

    return placeAutocomplete;
  }

  function handlePlaceSelection(form, event) {
    var prediction = event.placePrediction;

    if (!prediction) {
      clearPlaceSelection(form);
      showAddressError(form);
      return;
    }

    var place = prediction.toPlace();

    place
      .fetchFields({ fields: ["id", "addressComponents"] })
      .then(function () {
        if (!place.addressComponents || !place.addressComponents.length) {
          clearPlaceSelection(form);
          showAddressError(form);
          return;
        }

        fillAddressFields(form, place);
      })
      .catch(function () {
        clearPlaceSelection(form);
        showAddressError(form);
      });
  }

  function initAddressForm(form) {
    if (initializedForms.has(form)) {
      return;
    }

    var address1 = findField(form, "address_1");
    var placeId = findField(form, "place_id");

    if (
      !address1 ||
      !placeId ||
      !window.google ||
      !google.maps ||
      !google.maps.places ||
      !google.maps.places.PlaceAutocompleteElement
    ) {
      return;
    }

    initializedForms.add(form);

    var placeAutocomplete = mountAutocomplete(form, address1);
    ["address_1", "address_2", "city", "state", "zipcode"].forEach(function (fieldName) {
      lockAddressField(findField(form, fieldName));
    });

    placeAutocomplete.addEventListener("gmp-select", function (event) {
      handlePlaceSelection(form, event);
    });

    ["address_1", "city", "state", "zipcode"].forEach(function (fieldName) {
      var field = findField(form, fieldName);

      if (!field) {
        return;
      }

      field.addEventListener("input", function () {
        clearPlaceSelection(form);
      });

      field.addEventListener("change", function () {
        if (document.activeElement === field) {
          clearPlaceSelection(form);
        }
      });
    });

    form.addEventListener("submit", function (event) {
      if (placeId.value) {
        hideAddressError(form);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      enableSubmitButtons(form);
      showAddressError(form);
    });
  }

  window.initGoogleAddressAutocomplete = function () {
    var forms = document.querySelectorAll('[data-google-address-form="true"]');
    for (var i = 0; i < forms.length; i += 1) {
      initAddressForm(forms[i]);
    }
  };

  document.addEventListener("turbolinks:load", window.initGoogleAddressAutocomplete);
})();
